import { translate, translateLazy } from '@thoughtspot/i18n';
import { OptionsGroup } from '@thoughtspot/radiant-react/widgets/select/base-select';
import _ from 'lodash';
import {
    DateFilterType,
    DatePeriod,
    FilterTypes,
} from '/@services/generated/graphql-types';
import { DateFilterProto } from '../../date-filter';
import {
    getRollingDateFilterOperator,
    operatorToDateTypeAndFilterType,
    RollingDateFilterOperatorLabels,
    RollingDateFilterOperators,
} from './rolling-date-filter.util';

export const DEFAULT_DATE_TIME_UNIT = DatePeriod.Second;
export const DEFAULT_DATE_UNIT = DatePeriod.Day;

/**
Enumeration representing the list of dropdown option labels for rolling date filter operator dropdown.
@enum {string}
*/
export enum DateUnitsLabels {
    SECOND = 'dateFilter.second',
    MINUTE = 'dateFilter.minute',
    HOUR = 'dateFilter.hour',
    DAY = 'dateFilter.day',
    WEEK = 'dateFilter.week',
    MONTH = 'dateFilter.month',
    QUARTER = 'dateFilter.quarter',
    YEAR = 'dateFilter.year',
}

/**
Enumeration representing the list of dropdown option labels for rolling singular date filter operator dropdown.
@enum {string}
*/
export enum SingularDateUnitsLabels {
    SECOND = 'dateFilter.second_singular',
    MINUTE = 'dateFilter.minute_singular',
    HOUR = 'dateFilter.hour_singular',
    DAY = 'dateFilter.day_singular',
    WEEK = 'dateFilter.week_singular',
    MONTH = 'dateFilter.month_singular',
    QUARTER = 'dateFilter.quarter_singular',
    YEAR = 'dateFilter.year_singular',
}

/**
Enumeration representing the list of dropdown option labels for pre-operator dropdown.
@enum {string}
*/
export enum PreOperatorLabels {
    THIS = 'dateFilter.this',
    LAST = 'dateFilter.last',
    NEXT = 'dateFilter.next',
}

/**
 * types of expected parameters on operand value change event
 */
export type operandValues = {
    dateUnit?: DatePeriod;
    preOperator?: RollingDateFilterOperators;
    number?: number;
};

export const filterDropdownOptions = function filterDropdownOptions<OptionType>(
    options: { id: OptionType; label: string }[],
    keywordsToFilter: OptionType[],
) {
    return options.filter(option => !keywordsToFilter.includes(option.id));
};

export const timeBasedDateUnits: DatePeriod[] = [
    DatePeriod.Second,
    DatePeriod.Minute,
    DatePeriod.Hour,
];

// To identify filters with unsupported pre-operators.
export const futureDatePreOperators: RollingDateFilterOperators[] = [
    RollingDateFilterOperators.TOMORROW,
    RollingDateFilterOperators.NEXT,
];

export const futureDateOperators: RollingDateFilterOperators[] = [
    RollingDateFilterOperators.TOMORROW,
    RollingDateFilterOperators.NEXT,
];

/**
 * @param {boolean} hideTimeBasedFilters - Time based options like
 *   'second', 'minute', and 'hour' will be filtered out when `true`.
 *
 * Returns an array of objects representing dropdown options for date periods.
 *
 * @returns {OptionsGroup[]} An array of objects with a `options` property, which contains an array of objects representing the available date period options.
 */
export const getDateUnitDropdownOptions = (
    hideTimeBasedFilters: boolean,
): OptionsGroup[] => {
    let options = [
        {
            id: DatePeriod.Second,
            label: translate(DateUnitsLabels.SECOND),
        },
        {
            id: DatePeriod.Minute,
            label: translate(DateUnitsLabels.MINUTE),
        },
        {
            id: DatePeriod.Hour,
            label: translate(DateUnitsLabels.HOUR),
        },
        {
            id: DatePeriod.Day,
            label: translate(DateUnitsLabels.DAY),
        },
        {
            id: DatePeriod.Week,
            label: translate(DateUnitsLabels.WEEK),
        },
        {
            id: DatePeriod.Month,
            label: translate(DateUnitsLabels.MONTH),
        },
        {
            id: DatePeriod.Quarter,
            label: translate(DateUnitsLabels.QUARTER),
        },
        {
            id: DatePeriod.Year,
            label: translate(DateUnitsLabels.YEAR),
        },
    ];
    if (hideTimeBasedFilters) {
        options = filterDropdownOptions(options, timeBasedDateUnits);
    }
    return [{ options }];
};

/**
 * @param {boolean} hideTimeBasedFilters - Time based options like
 *   'seconds', 'minutes', and 'hours' will be filtered out when `true`.
 *
 * Returns an array of objects representing dropdown options for singular date periods.
 *
 * @returns {OptionsGroup[]} An array of objects with a `options` property, which contains an array of objects representing the available singular date period options.
 */
export const getSingularDateUnitDropdownOptions = (
    hideTimeBasedFilters: boolean,
): OptionsGroup[] => {
    let options = [
        {
            id: DatePeriod.Second,
            label: translate(SingularDateUnitsLabels.SECOND),
        },
        {
            id: DatePeriod.Minute,
            label: translate(SingularDateUnitsLabels.MINUTE),
        },
        {
            id: DatePeriod.Hour,
            label: translate(SingularDateUnitsLabels.HOUR),
        },
        {
            id: DatePeriod.Day,
            label: translate(SingularDateUnitsLabels.DAY),
        },
        {
            id: DatePeriod.Week,
            label: translate(SingularDateUnitsLabels.WEEK),
        },
        {
            id: DatePeriod.Month,
            label: translate(SingularDateUnitsLabels.MONTH),
        },
        {
            id: DatePeriod.Quarter,
            label: translate(SingularDateUnitsLabels.QUARTER),
        },
        {
            id: DatePeriod.Year,
            label: translate(SingularDateUnitsLabels.YEAR),
        },
    ];
    if (hideTimeBasedFilters) {
        options = filterDropdownOptions(options, timeBasedDateUnits);
    }
    return [{ options }];
};

interface Option {
    id: RollingDateFilterOperators;
    label: string;
}

const getPreOptions = (): Option[] => [
    {
        id: RollingDateFilterOperators.THIS,
        label: translate(PreOperatorLabels.THIS),
    },
    {
        id: RollingDateFilterOperators.LAST,
        label: translate(PreOperatorLabels.LAST),
    },
    {
        id: RollingDateFilterOperators.NEXT,
        label: translate(PreOperatorLabels.NEXT),
    },
];

const getSpecialPreOptions = (): Option[] => [
    {
        id: RollingDateFilterOperators.TODAY,
        label: translate(RollingDateFilterOperatorLabels.TODAY),
    },
    {
        id: RollingDateFilterOperators.TOMORROW,
        label: translate(RollingDateFilterOperatorLabels.TOMORROW),
    },
    {
        id: RollingDateFilterOperators.YESTERDAY,
        label: translate(RollingDateFilterOperatorLabels.YESTERDAY),
    },
];

const filterPreOptions = (
    options: Option[],
    hideFutureFilters: boolean,
): { id: RollingDateFilterOperators; label: string }[] => {
    return hideFutureFilters
        ? filterDropdownOptions(options, futureDatePreOperators)
        : options;
};

/**
 * @param {boolean} hideFutureFilters - Date operators that refer to
 *   future options like 'next' will be filtered out when `true`.
 *
 * Returns an array of options groups for a pre-operator dropdown
 *
 * @returns {OptionsGroup[]} An array of options groups for the pre-operator dropdown
 */
export const getPreOperatorDropdownOptions = (
    hideFutureFilters: boolean,
): OptionsGroup[] => {
    return [
        { options: filterPreOptions(getPreOptions(), hideFutureFilters) },
        {
            options: filterPreOptions(
                getSpecialPreOptions(),
                hideFutureFilters,
            ),
        },
    ];
};

/**
A mapping of RollingDateFilterOperators to corresponding DateFilterTypes.
*/
export const mapPerOperatorType: {
    [key in RollingDateFilterOperators]?: DateFilterType;
} = {
    [RollingDateFilterOperators.LAST]: DateFilterType.LastNPeriod,
    [RollingDateFilterOperators.NEXT]: DateFilterType.NextNPeriod,
    [RollingDateFilterOperators.THIS]: DateFilterType.ThisPeriod,
    [RollingDateFilterOperators.YESTERDAY]: DateFilterType.Yesterday,
    [RollingDateFilterOperators.TODAY]: DateFilterType.Today,
    [RollingDateFilterOperators.TOMORROW]: DateFilterType.Tomorrow,
};

/**
A mapping of DateFilterType to corresponding RollingDateFilterOperators.
*/
export const getPerOperatorTypeByPeriod: {
    [key in DateFilterType]?: RollingDateFilterOperators;
} = {
    [DateFilterType.LastNPeriod]: RollingDateFilterOperators.LAST,
    [DateFilterType.NextNPeriod]: RollingDateFilterOperators.NEXT,
    [DateFilterType.ThisPeriod]: RollingDateFilterOperators.THIS,
    [DateFilterType.Yesterday]: RollingDateFilterOperators.YESTERDAY,
    [DateFilterType.Today]: RollingDateFilterOperators.TODAY,
    [DateFilterType.Tomorrow]: RollingDateFilterOperators.TOMORROW,
};

export const updateDefaultRollingFilterValues = () => {
    // we need to change the selected filter on tab change with the already selected values
    // or if nothing is selected then with default values
    const filterOperator = getRollingDateFilterOperator(
        FilterTypes.Eq,
        DateFilterType.Yesterday,
        null,
    );
    return {
        ...operatorToDateTypeAndFilterType[filterOperator],
    };
};

export const getRollingDateFilterPreOperator = (filter: DateFilterProto) =>
    filter.filterType && getPerOperatorTypeByPeriod[filter.dateFilterType];

/**
 * Determines if the pre-operator with a date unit should be shown.
 *
 * @param {DateFilterType} dateFilterType - The type of date filter being used.
 * @param {RollingDateFilterOperators} selectedPreOperator - The pre-operator selected for the date filter.
 * @returns {boolean} - Returns true if the pre-operator with a date unit should be shown, otherwise false.
 */
export const shouldShowPreOperatorWithDateUnit = (
    dateFilterType: DateFilterType,
    selectedPreOperator: RollingDateFilterOperators,
): boolean => {
    const specificDateFilterTypes = [
        DateFilterType.NextNPeriod,
        DateFilterType.LastNPeriod,
        DateFilterType.ThisPeriod,
    ];

    const specificPreOperators = [
        RollingDateFilterOperators.YESTERDAY,
        RollingDateFilterOperators.TODAY,
        RollingDateFilterOperators.TOMORROW,
    ];

    const isSpecificDateFilterType = specificDateFilterTypes.includes(
        dateFilterType,
    );
    const isNotSpecificPreOperator = !specificPreOperators.includes(
        selectedPreOperator,
    );

    return isSpecificDateFilterType && isNotSpecificPreOperator;
};
